import { useState, useEffect, useCallback } from 'react';
import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { log } from '../services/production-logger';


interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface UsePWAInstallReturn {
  isInstallable: boolean;
  isInstalled: boolean;
  isIOS: boolean;
  showBanner: boolean;
  showModal: boolean;
  installPromptEvent: BeforeInstallPromptEvent | null;
  dismissBanner: () => void;
  dismissModal: () => void;
  showInstallModal: () => void;
  handleInstall: () => Promise<void>;
  dontShowAgain: () => Promise<void>;
}

const PWA_INSTALL_DISMISSED_KEY = 'pwa_install_dismissed';
const PWA_INSTALL_DISMISSED_DATE_KEY = 'pwa_install_dismissed_date';
const PWA_INSTALL_SHOWN_COUNT_KEY = 'pwa_install_shown_count';
const DISMISS_DURATION_DAYS = 30;
const MIN_ENGAGEMENT_TIME = 30000; // 30 seconds
const MIN_PAGE_VIEWS = 2;

export function usePWAInstall(): UsePWAInstallReturn {
  const [installPromptEvent, setInstallPromptEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [pageViews, setPageViews] = useState(0);
  const [engagementTime, setEngagementTime] = useState(0);

  const isIOS = Platform.OS === 'ios' && Platform.select({ web: true, default: false });
  const isInstallable = !isInstalled && (!!installPromptEvent || isIOS);

  // Check if app is already installed
  useEffect(() => {
    if (Platform.OS !== 'web') return;

    // Check if running in standalone mode
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
      || (window.navigator as any).standalone === true;
    
    setIsInstalled(isStandalone);

    // Listen for app installed event
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setShowBanner(false);
      setShowModal(false);
    };

    window.addEventListener('appinstalled', handleAppInstalled);
    return () => window.removeEventListener('appinstalled', handleAppInstalled);
  }, []);

  // Listen for beforeinstallprompt event
  useEffect(() => {
    if (Platform.OS !== 'web') return;

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setInstallPromptEvent(e as BeforeInstallPromptEvent);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  }, []);

  // Track page views
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    setPageViews(prev => prev + 1);
  }, []);

  // Track engagement time
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    
    const startTime = Date.now();
    const interval = setInterval(() => {
      setEngagementTime(Date.now() - startTime);
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Check if we should show the banner
  useEffect(() => {
    if (Platform.OS !== 'web' || isInstalled || !isInstallable) return;

    const checkShouldShowBanner = async () => {
      try {
        // Check if user dismissed permanently
        const dismissedPermanently = await AsyncStorage.getItem(PWA_INSTALL_DISMISSED_KEY);
        if (dismissedPermanently === 'true') {
          const dismissedDate = await AsyncStorage.getItem(PWA_INSTALL_DISMISSED_DATE_KEY);
          if (dismissedDate) {
            const daysSinceDismissed = (Date.now() - parseInt(dismissedDate)) / (1000 * 60 * 60 * 24);
            if (daysSinceDismissed < DISMISS_DURATION_DAYS) {
              return;
            }
          }
        }

        // Check engagement criteria
        if (pageViews >= MIN_PAGE_VIEWS && engagementTime >= MIN_ENGAGEMENT_TIME) {
          // Increment shown count
          const shownCount = await AsyncStorage.getItem(PWA_INSTALL_SHOWN_COUNT_KEY);
          const count = shownCount ? parseInt(shownCount) : 0;
          await AsyncStorage.setItem(PWA_INSTALL_SHOWN_COUNT_KEY, (count + 1).toString());
          
          setShowBanner(true);
        }
      } catch (error) {
        log.error('Error checking PWA install banner criteria:', error);
      }
    };

    checkShouldShowBanner();
  }, [pageViews, engagementTime, isInstallable, isInstalled]);

  const dismissBanner = useCallback(() => {
    setShowBanner(false);
  }, []);

  const dismissModal = useCallback(() => {
    setShowModal(false);
  }, []);

  const showInstallModal = useCallback(() => {
    setShowBanner(false);
    setShowModal(true);
  }, []);

  const handleInstall = useCallback(async () => {
    if (isIOS) {
      // iOS requires manual installation
      // The modal will show instructions
      return;
    }

    if (!installPromptEvent) {
      log.error('No install prompt event available');
      return;
    }

    try {
      await installPromptEvent.prompt();
      const { outcome } = await installPromptEvent.userChoice;
      
      if (outcome === 'accepted') {
        log.debug('PWA installation accepted');
        setShowModal(false);
      } else {
        log.debug('PWA installation dismissed');
      }
    } catch (error) {
      log.error('Error during PWA installation:', error);
    }
  }, [installPromptEvent, isIOS]);

  const dontShowAgain = useCallback(async () => {
    try {
      await AsyncStorage.setItem(PWA_INSTALL_DISMISSED_KEY, 'true');
      await AsyncStorage.setItem(PWA_INSTALL_DISMISSED_DATE_KEY, Date.now().toString());
      setShowBanner(false);
      setShowModal(false);
    } catch (error) {
      log.error('Error saving PWA install preferences:', error);
    }
  }, []);

  return {
    isInstallable,
    isInstalled,
    isIOS,
    showBanner,
    showModal,
    installPromptEvent,
    dismissBanner,
    dismissModal,
    showInstallModal,
    handleInstall,
    dontShowAgain,
  };
}